import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase'; // Adjust path as necessary
import {   Container, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Grid, Box, Card, CardContent, 
  IconButton, Tooltip, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface BotPerformanceStat {
  id: number;
  bot_id: string;
  strategy_name?: string;
  symbol?: string;
  timeframe?: string;
  current_price?: number;
  pnl: number;
  win_rate: number;
  trades: number;
  equity: number;
  balance?: number;
  floating_pnl?: number;
  drawdown: number;
  recorded_at: string;
  created_at: string;
}

const BotPerformancePage: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<BotPerformanceStat[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Process data for charts with sampling for better performance
  const prepareChartData = () => {
    if (!performanceData.length) return null;
      // Sort data by date for time-series charts - ensure chronological order (oldest to newest)
    const sortedData = [...performanceData].sort((a, b) => 
      new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
    );
    
    // Enhanced adaptive sampling for better chart performance
    let dataToUse = sortedData;
    
    // Apply sampling if we have more than 50 points
    const shouldSample = sortedData.length > 50;
    
    if (shouldSample && sortedData.length > 20) { // Keep the > 20 check to avoid sampling tiny datasets unnecessarily
      // Determine target sample size based on data volume
      let targetPoints = 50;
      if (sortedData.length > 500) targetPoints = 100;
      else if (sortedData.length > 200) targetPoints = 75;
      else if (sortedData.length > 100) targetPoints = 60;
      
      // Always keep first and last data points
      const first = sortedData[0];
      const last = sortedData[sortedData.length - 1];
      
      // Find critical points (max/min for equity and PNL)
      const maxEquityPoint = sortedData.reduce(
        (max, p) => (p.equity > max.equity ? p : max),
        sortedData[0]
      );
      const minEquityPoint = sortedData.reduce(
        (min, p) => (p.equity < min.equity ? p : min),
        sortedData[0]
      );
      const maxPnlPoint = sortedData.reduce(
        (max, p) => (p.pnl > max.pnl ? p : max),
        sortedData[0]
      );
      const minPnlPoint = sortedData.reduce(
        (min, p) => (p.pnl < min.pnl ? p : min),
        sortedData[0]
      );
      
      // Create set of key points to always include
      const keyPointsSet = new Set([
        first.id, 
        last.id,
        maxEquityPoint.id,
        minEquityPoint.id,
        maxPnlPoint.id,
        minPnlPoint.id
      ]);
      const keyPoints = [first, last, maxEquityPoint, minEquityPoint, maxPnlPoint, minPnlPoint]
        .filter((p, i, arr) => arr.findIndex(item => item.id === p.id) === i); // Remove duplicates
      
      // Calculate sampling interval for remaining points
      const remainingPoints = Math.max(0, targetPoints - keyPoints.length);
      const sampleInterval = Math.max(1, Math.floor(sortedData.length / remainingPoints));
      
      // Sample additional points at regular intervals
      const sampledPoints = [];
      for (let i = 0; i < sortedData.length; i += sampleInterval) {
        if (!keyPointsSet.has(sortedData[i].id) && sampledPoints.length < remainingPoints) {
          sampledPoints.push(sortedData[i]);
        }
      }
      
      // Combine and sort all points
      dataToUse = [...keyPoints, ...sampledPoints].sort((a, b) => 
        new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
      );
    }
    
    // Extract dates, equity, and PNL for line charts
    const dates = dataToUse.map(d => new Date(d.recorded_at).toLocaleString());
    const equity = dataToUse.map(d => d.equity);
    const pnl = dataToUse.map(d => d.pnl);
    const balance = dataToUse.map(d => d.balance || 0);
    
    // Calculate win vs. loss trades for doughnut chart
    const latestStat = sortedData[sortedData.length - 1]; // Always use the latest stat for this
    const winTrades = Math.round(latestStat.trades * latestStat.win_rate);
    const lossTrades = latestStat.trades - winTrades;
      return { 
      dates, 
      equity, 
      pnl, 
      balance, 
      winTrades, 
      lossTrades,      dataPointCount: dataToUse.length,
      totalDataPoints: sortedData.length
    };
  };
  
  // Auto-calculated data limit based on device performance  
  const [dataLimit] = useState<number>(100); 
  const [tableLimit] = useState<number>(20); // Limit table rows to avoid overwhelming the UI
  
  useEffect(() => {
    const fetchPerformanceData = async () => {
      setLoading(true);
      setError(null);
      try {
        // We fetch more than we need to ensure we get the full history
        // This ensures we have a complete history for charts without overwhelming database
        const { data, error: supabaseError } = await supabase
          .from('bot_performance_stats')
          .select('*')
          .order('recorded_at', { ascending: false }) // Get latest records first for table display
          .limit(Math.max(500, dataLimit)); // Fetch enough data for good chart visualization

        if (supabaseError) {
          throw supabaseError;
        }
        setPerformanceData(data || []);
      } catch (err: any) {
        console.error('Error fetching bot performance data:', err);
        setError(err.message || 'Failed to fetch performance data.');
      } finally {        setLoading(false);
      }
    };
    fetchPerformanceData();
  }, [dataLimit]);
  
  // Prepare chart data
  const chartData = prepareChartData();  // Handle chart data mode changes only - data limit is handled automatically
  

  const refreshData = () => {
    // Re-fetch the data
    const fetchPerformanceData = async () => {
      setLoading(true);
      try {
        const { data, error: supabaseError } = await supabase
          .from('bot_performance_stats')
          .select('*')
          .order('recorded_at', { ascending: false })
          .limit(Math.max(500, dataLimit)); // Match the format used in the initial fetch

        if (supabaseError) {
          throw supabaseError;
        }
        setPerformanceData(data || []);
      } catch (err: any) {
        console.error('Error refreshing bot performance data:', err);
        setError(err.message || 'Failed to refresh performance data.');
      } finally {
        setLoading(false);
      }
    };
    fetchPerformanceData();
  };
  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            Bot Performance Statistics
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">            
            <Tooltip title="Refresh Data">
              <IconButton onClick={refreshData} color="primary" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {!loading && !error && performanceData.length === 0 && (
          <Typography sx={{ mt: 2 }}>No performance data available yet.</Typography>
        )}
          {!loading && !error && performanceData.length > 0 && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {performanceData.length > 0 && (
                <>
                  <Grid item xs={12} md={3}>
                    <Card raised>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Current Equity
                        </Typography>
                        <Typography variant="h5">
                          ${performanceData[0].equity.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card raised>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Total P&L
                        </Typography>
                        <Typography variant="h5" color={performanceData[0].pnl >= 0 ? 'success.main' : 'error.main'}>
                          ${performanceData[0].pnl.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card raised>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Win Rate
                        </Typography>                        <Typography variant="h5">
                          {(performanceData[0].win_rate * 100).toFixed(2)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card raised>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Total Trades
                        </Typography>
                        <Typography variant="h5">
                          {performanceData[0].trades}
                        </Typography>                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Charts */}
            {chartData && (
              <>                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Equity Over Time Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Equity Over Time</Typography>
                    <Box sx={{ height: 300 }}>
                      <Line
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' },
                            tooltip: {
                              callbacks: {
                                title: (items) => {
                                  if (!items.length) return '';
                                  const index = items[0].dataIndex;
                                  return chartData.dates[index];
                                }
                              }
                            }
                          }
                        }}
                        data={{
                          labels: chartData.dates,
                          datasets: [
                            {
                              label: 'Equity',
                              data: chartData.equity,
                              borderColor: 'rgb(53, 162, 235)',
                              backgroundColor: 'rgba(53, 162, 235, 0.5)',
                            }
                          ]
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* P&L Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>P&L Performance</Typography>
                    <Box sx={{ height: 300 }}>
                      <Bar
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' }
                          }
                        }}
                        data={{
                          labels: chartData.dates,
                          datasets: [
                            {
                              label: 'Profit/Loss',
                              data: chartData.pnl,
                              backgroundColor: chartData.pnl.map(value => 
                                value >= 0 ? 'rgba(75, 192, 192, 0.7)' : 'rgba(255, 99, 132, 0.7)'
                              )
                            }
                          ]
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Win Rate Doughnut Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Win/Loss Distribution</Typography>
                    <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                      <Doughnut
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        data={{
                          labels: ['Winning Trades', 'Losing Trades'],
                          datasets: [
                            {
                              data: [chartData.winTrades, chartData.lossTrades],
                              backgroundColor: [
                                'rgba(75, 192, 192, 0.7)',
                                'rgba(255, 99, 132, 0.7)',
                              ],
                              borderColor: [
                                'rgba(75, 192, 192, 1)',
                                'rgba(255, 99, 132, 1)',
                              ],
                              borderWidth: 1,
                            }
                          ]
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                {/* Balance vs Equity comparison */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Balance vs Equity</Typography>
                    <Box sx={{ height: 300 }}>
                      <Line
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' }
                          }
                        }}
                        data={{
                          labels: chartData.dates,
                          datasets: [
                            {
                              label: 'Balance',
                              data: chartData.balance,
                              borderColor: 'rgb(255, 159, 64)',
                              backgroundColor: 'rgba(255, 159, 64, 0.5)',
                              borderDash: [5, 5]
                            },
                            {
                              label: 'Equity',
                              data: chartData.equity,
                              borderColor: 'rgb(75, 192, 192)',
                              backgroundColor: 'rgba(75, 192, 192, 0.5)',
                            }
                          ]
                        }}
                      />
                    </Box>
                  </Paper>                </Grid>
              </Grid>
            </>
            )}            {/* Table of raw data */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
              <Typography variant="h5" gutterBottom>
                Performance History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {Math.min(tableLimit, performanceData.length)} most recent records of {performanceData.length} total
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table aria-label="bot performance table">
                <TableHead>
                  <TableRow>
                    <TableCell>Bot ID</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Timeframe</TableCell>
                    <TableCell align="right">Recorded At</TableCell>
                    <TableCell align="right">P&L</TableCell>
                    <TableCell align="right">Win Rate (%)</TableCell>
                    <TableCell align="right">Trades</TableCell>
                    <TableCell align="right">Equity</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Drawdown (%)</TableCell>
                  </TableRow>
                </TableHead>                <TableBody>
                  {/* Only display a limited number of rows in the table */}
                  {performanceData.slice(0, tableLimit).map((stat) => (
                    <TableRow key={stat.id}>
                      <TableCell component="th" scope="row">
                        {stat.bot_id}
                      </TableCell>
                      <TableCell>{stat.symbol || 'N/A'}</TableCell>
                      <TableCell>{stat.timeframe || 'N/A'}</TableCell>
                      <TableCell align="right">{new Date(stat.recorded_at).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ 
                        color: stat.pnl >= 0 ? 'success.main' : 'error.main'
                      }}>
                        {stat.pnl.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{(stat.win_rate * 100).toFixed(2)}</TableCell>
                      <TableCell align="right">{stat.trades}</TableCell>
                      <TableCell align="right">{stat.equity.toFixed(2)}</TableCell>
                      <TableCell align="right">{stat.balance?.toFixed(2) || 'N/A'}</TableCell>
                      <TableCell align="right">{(stat.drawdown * 100).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}</TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
    </>
  );
};

export default BotPerformancePage;
